/**
 * AccountantSendDialog
 * --------------------
 * Reusable confirm dialog for sending invoices to the user's accountant.
 *
 * Handles three states:
 *  1) Profile not loaded yet → loading skeleton
 *  2) No accountant_email set → CTA to Settings
 *  3) Has email but no consent yet → checkbox + consent text + send button
 *  4) Has email + consent → simple confirmation
 *
 * On confirm: writes accountant_consent_at if newly given, then invokes the
 * `send-invoices-to-accountant` edge function with the supplied invoice ids.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Send, AlertCircle, Mail, Settings as SettingsIcon } from "lucide-react";
import { toast } from "sonner";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoiceIds: string[];
  /** Optional human label for context, e.g. "Factura 2026-001" or "12 facturi selectate". */
  contextLabel?: string;
  /** Called after a successful send. */
  onSent?: () => void;
}

const AccountantSendDialog = ({ open, onOpenChange, invoiceIds, contextLabel, onSent }: Props) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [consentChecked, setConsentChecked] = useState(false);
  const [sending, setSending] = useState(false);

  const { data: profile, isLoading } = useQuery({
    queryKey: ["profile-accountant", user?.id],
    enabled: !!user && open,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("accountant_email, accountant_consent_at, company_name, display_name")
        .eq("user_id", user!.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const hasEmail = !!profile?.accountant_email;
  const hasConsent = !!profile?.accountant_consent_at;
  const needsConsent = hasEmail && !hasConsent;

  const handleSend = async () => {
    if (!user) return;
    if (needsConsent && !consentChecked) {
      toast.error("Bifează acordul GDPR pentru a continua.");
      return;
    }

    setSending(true);
    try {
      // Persist consent on first send
      if (needsConsent && consentChecked) {
        const { error: consentErr } = await supabase
          .from("profiles")
          .update({ accountant_consent_at: new Date().toISOString() })
          .eq("user_id", user.id);
        if (consentErr) throw consentErr;
        queryClient.invalidateQueries({ queryKey: ["profile-accountant", user.id] });
        queryClient.invalidateQueries({ queryKey: ["profile", user.id] });
      }

      const { data, error } = await supabase.functions.invoke("send-invoices-to-accountant", {
        body: {
          invoice_ids: invoiceIds,
          send_type: "manual",
        },
      });

      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);

      const sent = (data as any)?.invoice_count ?? invoiceIds.length;
      toast.success(
        `${sent} ${sent === 1 ? "factură trimisă" : "facturi trimise"} către contabil. Vei primi și tu o copie pe email.`,
      );
      onOpenChange(false);
      onSent?.();
    } catch (err: any) {
      const msg = err?.message || "";
      if (msg.includes("no_files_available")) {
        toast.error("Fișierele nu sunt disponibile. Deschide fiecare factură o dată ca să fie generate, apoi reîncearcă.");
      } else {
        toast.error(msg || "Eroare la trimiterea facturilor către contabil");
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="h-5 w-5 text-primary" />
            Trimite la contabil
          </DialogTitle>
          <DialogDescription>
            {contextLabel
              ? `Vor fi trimise: ${contextLabel}.`
              : `Vor fi trimise ${invoiceIds.length} ${invoiceIds.length === 1 ? "factură" : "facturi"}.`}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="py-6 flex justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : !hasEmail ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 flex gap-3">
              <AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
              <div className="text-sm space-y-1">
                <p className="font-semibold text-foreground">Nu ai setat un email de contabil</p>
                <p className="text-muted-foreground">
                  Adaugă mai întâi adresa contabilului din pagina de Setări.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Închide
              </Button>
              <Button onClick={() => { onOpenChange(false); navigate("/settings"); }}>
                <SettingsIcon className="h-4 w-4 mr-2" /> Mergi la Setări
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg border border-border bg-card p-4 flex gap-3">
              <Mail className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <div className="text-sm space-y-0.5">
                <p className="text-muted-foreground">Către:</p>
                <p className="font-medium text-foreground break-all">{profile.accountant_email}</p>
                <p className="text-xs text-muted-foreground pt-1">
                  Vei primi și tu o copie pe email.
                </p>
              </div>
            </div>

            {needsConsent && (
              <label className="flex gap-3 rounded-lg border border-primary/30 bg-primary/5 p-3 cursor-pointer">
                <Checkbox
                  checked={consentChecked}
                  onCheckedChange={(v) => setConsentChecked(v === true)}
                  className="mt-0.5"
                />
                <span className="text-xs text-muted-foreground leading-relaxed">
                  Sunt de acord ca <strong className="text-foreground">EZ Factura</strong> să transmită
                  către contabilul meu (
                  <span className="text-foreground">{profile.accountant_email}</span>) facturile mele,
                  prin email, sub formă de arhivă ZIP cu link de descărcare valabil 30 de zile. Acest
                  acord este necesar conform GDPR (art. 6(1)(a)) și poate fi retras oricând din pagina{" "}
                  <a
                    href="/settings"
                    className="text-primary hover:underline"
                    onClick={(e) => { e.preventDefault(); onOpenChange(false); navigate("/settings"); }}
                  >
                    Setări
                  </a>{" "}
                  sau scriindu-ne la contact@ezfactura.ro. Detalii în{" "}
                  <a
                    href="/privacy"
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary hover:underline"
                  >
                    Politica de Confidențialitate
                  </a>.
                </span>
              </label>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>
                Renunță
              </Button>
              <Button
                onClick={handleSend}
                disabled={sending || (needsConsent && !consentChecked)}
              >
                {sending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Trimite acum
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AccountantSendDialog;
